import { useEffect, useCallback, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  updateSettings,
  loadSettingsFromRemoteStore,
  resetSingleSetting,
  computeApiState,
  apiStatusType,
} from 'redux/reducers/persistentSettings';
import { RootState } from 'redux/rootReducer';
import { TAsyncDispatchResult } from 'types/shared';
import { ObjectSchema } from 'yup';

export default useComponentSettings;

type TUseComponentSettings<T> = [
  T,
  (settings: T) => TAsyncDispatchResult,
  (newSettings: T) => TAsyncDispatchResult,
  apiStatusType,
  boolean
];

/**
 * This hook is used to make settings from components persist across page loads.
 * It will sync the settings you pass into it to a local storage provider through localForage while saving
 * the settings to the K/V store in parallel. We use the local storage provider to provide an instant responsive settings,
 * while using the K/V store to make sure your settings are shared across browsers/devices.
 *
 * You cannot synchronize component state that relies on asynchronous API requests in the same component. Trying to do
 * so will result in race errors. It's best to write your component state in a way that is not reduced from asynchronous data.
 *
 * When designing your component settings, and the parts of it that will be stored, you need to take special care that
 * all of the different options for a settings have the exact same shape. As an example, if you need to store a
 * user-selected sales type, the objects for sales types in src/maps/sales-types.js have different shapes. In a case like
 * that, it is better to store a simple string unique identifier, like the `value` key of a salesType with which you refer
 * to the object.
 *
 * We need to ensure correctness of the user settings that are saved in the KV store and localForage cache.
 * That's why we have to provide a custom written validator schema. This schema needs to be updated every time you
 * change, remove or add possible user settings for this component.
 *
 * @param settingsId       A unique ID for these component settings. When using the same id, you can share settings between different component instances.
 * @param schema           An object schema built with yup, used to validate incoming data and return default values if the validator fails
 * @return - Array containing the settings, a setter, and a debug function to clear the API settings completely for the logged in user
 * @return Respectively a getter, setter and function that clears the components settings in API for the user
 */
function useComponentSettings<T extends object>(
  settingsId: string,
  schema: ObjectSchema<T>,
  compareMode?: string
): TUseComponentSettings<T> {
  // Some basic error checking to see if people use this hook correctly.
  if (typeof settingsId !== 'string' || settingsId === '') {
    // eslint-disable-next-line no-console
    console.error(
      'useComponentSettings: You need to pass a string which is the component ID as the first argument. We will try to return the default settings so your component does not crash, but you can not use a setter.'
    );
  }

  // Some basic error checking to see if people use this hook correctly.
  if (!schema) {
    // eslint-disable-next-line no-console
    console.error(
      'useComponentSettings: You need to pass a yup schema as the first argument. Your component settings will not be validated now.'
    );
  }

  // Once isInValidated the hook will not try to reset the component settings again.
  // This prevents an infinite loop when the default schema value doesn't match the actual schema
  const isInvalidated = useRef(false);

  const isLoaded = useRef(false);

  /**
   * Memoize the schema, so we don't trigger the validator effect all the time
   */
  const validator = useRef<ObjectSchema<T>>(schema);

  /**
   * Memoize action creators
   */
  const dispatch = useDispatch();
  const setComponentSettings = useCallback(
    (settings: T): TAsyncDispatchResult =>
      dispatch(updateSettings(settingsId, settings)),
    [settingsId, dispatch]
  );

  const getComponentSettings = useCallback(
    () => dispatch(loadSettingsFromRemoteStore()),
    [dispatch]
  );

  const resetComponentSettings = useCallback(
    (newSettings: T): TAsyncDispatchResult =>
      dispatch(resetSingleSetting(settingsId, newSettings)),
    [settingsId, dispatch]
  );

  /**
   * Get the currently persisted settings
   */
  const persistedSettings = useSelector(
    (state: RootState): T => state.persistentSettings.settings[settingsId]
  );

  /**
   * Get the current status of the API request to save the settings
   */
  const apiStatus: apiStatusType = useSelector((state: RootState): any =>
    computeApiState(state.persistentSettings)
  );

  /**
   * For the first render, we return the default settings, or the schema.default()
   * When the  persistedSettings come in from the KV store and they are validated we update the
   * settings state value and return that
   */
  const [settings, setSettings] = useState<T>(validator.current.default());

  /**
   * Get the persisted settings from the API if they weren't loaded yet.
   *
   * We fire this once, and only if the API has not been loaded or started to load yet.
   * In the future, we might want to reduce this more, e.g. check for last date/time of
   * sync and only sync when older than 1 minute.
   */
  useEffect(() => {
    if (apiStatus === 'unloaded') getComponentSettings();
  }, [apiStatus, getComponentSettings]);

  /**
   * Whenever the settings change – e.g. they come in from the KV store or from
   * the IndexedDB cache – we need to validate if they are correct.
   */
  useEffect(() => {
    const logPrefix = `User settings: ${settingsId}`;

    const schema = validator.current;

    if (persistedSettings) {
      /* eslint-disable no-console */

      // If there's a schema, we return the validated persistedSettings
      // This will automatically fill in missing values with defaults and
      // use those in the component. It will not automatically save to the API
      // when using default values
      // When schema validation fails we show a message in the log, but we don't have to do
      // any state control, because the schema.default() settings are already loaded in this hooks state.
      if (schema) {
        // Merge the persistedSettings with the defaults to make sure
        // that the settings object structure is always complete.
        const settingsToValidate = {
          ...schema.default(),
          ...persistedSettings,
        };

        schema
          .validate(settingsToValidate, {
            // Make sure we do strict validation. This way Yup doesn't do coercion.
            // @see https://github.com/jquense/yup#mixedstrictisstrict-boolean--false-schema
            strict: true,
            context: {
              compareMode,
            },
          })
          .then(function setValidatedSettings(validatedSettings: any) {
            setSettings(() => {
              isInvalidated.current = false;
              isLoaded.current = true;

              return validatedSettings;
            });
          })
          .catch((validationError: any) => {
            console.warn(
              `${logPrefix}: ❌ Your component settings for ${settingsId} are not valid, because of this error:`,
              validationError
            );
            console.warn(
              `${logPrefix}: Reverting to these component settings:`,
              schema.default()
            );

            if (!isInvalidated.current) {
              resetComponentSettings(schema.default());

              isInvalidated.current = true;
            }
          });
      }
      /* eslint-enable no-console */
    } else {
      isLoaded.current = true;
    }
  }, [settingsId, persistedSettings, resetComponentSettings, compareMode]);

  /**
   * If the shape of the default settings and the current component settings
   * is not similar, we should reset the component settings
   * to have the same shape as the default settings.
   *
   * The assumption is that, while we want to retain a users' settings as much
   * as possible, when the shape of the default settings changes, it is very
   * likely that the component is being updated and we need to update the
   * shape of the settings accordingly.
   *
   * We want to do this check synchronously, before committing the componentSettings
   * to the component that uses this hook. We also want to run this check on both the
   * local IndexedDB and the remote copy of the component state.
   */
  return [settings, setComponentSettings, resetComponentSettings, apiStatus, isLoaded.current];
}
